import { Button, Input } from '@assembly-js/design-system';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import { ErrorMessage } from 'src/components/ErrorMessage';
import { SuggestIconPicker } from 'src/features/SuggestIconPicker';
import { useUpdateAppSettings } from 'src/hooks/useUpdateAppSettings';

interface RenameAppFormProps {
  moduleId: string;
  onClose: () => void;
}
const NAME_ERROR = 'Name required';
const MAX_NAME_LENGTH = 50;
export const RenameAppForm = ({ moduleId, onClose }: RenameAppFormProps) => {
  const { updateSettings, currentTitle, currentIcon } =
    useUpdateAppSettings(moduleId);

  const [name, setName] = useState(currentTitle);
  const [icon, setIcon] = useState(currentIcon ?? 'dashboard');
  const [nameError, setNameError] = useState<string | null>(null);
  const initialized = useRef(false);
  const initialIconRef = useRef(currentIcon ?? 'dashboard');
  const trimmedName = name.trim();
  const hasChanges =
    trimmedName !== currentTitle || icon !== initialIconRef.current;

  useEffect(() => {
    if (!initialized.current && currentTitle) {
      const resolvedIcon = currentIcon ?? 'dashboard';
      setName(currentTitle);
      setIcon(resolvedIcon);
      initialIconRef.current = resolvedIcon;
      initialized.current = true;
    }
  }, [currentTitle, currentIcon]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val.length > MAX_NAME_LENGTH) return;
    setName(val);
    if (!val.trim()) setNameError(NAME_ERROR);
    else if (nameError) setNameError(null);
  };

  const handleNameBlur = () => {
    if (!name.trim()) setNameError(NAME_ERROR);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!trimmedName) {
      setNameError(NAME_ERROR);
      return;
    }
    onClose();
    updateSettings({ name: trimmedName, icon });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex items-start gap-2">
        <SuggestIconPicker
          value={icon}
          onChange={setIcon}
          appName={name}
          className="w-7 h-7 shrink-0 mt-px"
        />
        <div className="flex-1 min-w-0 flex flex-col">
          <Input
            autoFocus
            className={clsx('h-7 text-sm', nameError && 'border-error-primary')}
            value={name}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            maxLength={MAX_NAME_LENGTH}
          />
          {nameError && (
            <ErrorMessage message={nameError} className="text-sm" />
          )}
        </div>
        <Button
          type="submit"
          size="sm"
          label="Save"
          className="h-7 shrink-0 mt-px"
          disabled={!trimmedName || !hasChanges}
        />
      </div>
    </form>
  );
};
